import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Phone, Building, ArrowRight, X } from 'lucide-react';
import Button from './ui/Button';
import GradientText from './ui/GradientText';

interface GoogleOAuthAdditionalInfoProps {
  onComplete: (additionalInfo: { phone?: string; enterprise?: string; rgpdConsent?: boolean; newsletterConsent?: boolean }) => void;
  onSkip: () => void;
  loading?: boolean;
  error?: string;
}

const GoogleOAuthAdditionalInfo: React.FC<GoogleOAuthAdditionalInfoProps> = ({
  onComplete,
  onSkip,
  loading = false,
  error = ''
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    phone: '',
    enterprise: '',
    rgpdConsent: false,
    newsletterConsent: false
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.rgpdConsent) {
      return;
    }
    
    const additionalInfo: { phone?: string; enterprise?: string; rgpdConsent?: boolean; newsletterConsent?: boolean } = {};
    
    if (formData.phone.trim()) {
      additionalInfo.phone = formData.phone.trim();
    }
    
    if (formData.enterprise.trim()) {
      additionalInfo.enterprise = formData.enterprise.trim();
    }
    
    additionalInfo.rgpdConsent = formData.rgpdConsent;
    additionalInfo.newsletterConsent = formData.newsletterConsent;
    
    onComplete(additionalInfo);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t('auth.google.additionalInfo.title', 'Complete Your Profile')}
            </h2>
            <p className="text-gray-600">
              {t('auth.google.additionalInfo.subtitle', 'Help us personalize your experience (optional)')}
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
        >
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.google.additionalInfo.phone', 'Phone Number')}
                <span className="text-gray-400 text-xs ml-1">
                  {t('auth.google.additionalInfo.optional', '(optional)')}
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="appearance-none rounded-lg relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10"
                  placeholder={t('auth.google.additionalInfo.phonePlaceholder', 'Enter your phone number')}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="enterprise" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.google.additionalInfo.company', 'Company')}
                <span className="text-gray-400 text-xs ml-1">
                  {t('auth.google.additionalInfo.optional', '(optional)')}
                </span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="enterprise"
                  name="enterprise"
                  type="text"
                  value={formData.enterprise}
                  onChange={(e) => handleInputChange('enterprise', e.target.value)}
                  className="appearance-none rounded-lg relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:z-10"
                  placeholder={t('auth.google.additionalInfo.companyPlaceholder', 'Enter your company name')}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="rgpdConsent"
                    name="rgpdConsent"
                    type="checkbox"
                    checked={formData.rgpdConsent}
                    onChange={(e) => handleInputChange('rgpdConsent', e.target.checked)}
                    className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                    disabled={loading}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="rgpdConsent" className="text-gray-700">
                    {t('auth.register.consent.privacy', 'I authorize Kexa team to keep my contact information for management and support purposes.')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="newsletterConsent"
                    name="newsletterConsent"
                    type="checkbox"
                    checked={formData.newsletterConsent}
                    onChange={(e) => handleInputChange('newsletterConsent', e.target.checked)}
                    className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2"
                    disabled={loading}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="newsletterConsent" className="text-gray-700">
                    {t('auth.register.consent.newsletter', 'I would like to receive updates and news about Kexa.')}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="flex-1"
                rightIcon={<ArrowRight size={20} />}
                disabled={loading || !formData.rgpdConsent}
              >
                {loading 
                  ? t('auth.google.additionalInfo.saving', 'Saving...') 
                  : t('auth.google.additionalInfo.complete', 'Complete Setup')
                }
              </Button>
              
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onSkip}
                disabled={loading}
                className="px-4"
              >
                {t('auth.google.additionalInfo.skip', 'Skip')}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              {t('auth.google.additionalInfo.note', 'You can always update this information later in your account settings.')}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default GoogleOAuthAdditionalInfo;